import { ref, computed, nextTick } from 'vue';
import { useTaskAssigneeOptions } from './useTaskAssigneeOptions';
import { toast } from 'vue-sonner';
import { Api } from '@/api';

export type OneOffTaskInputPayload = {
  name: string;
  assigneeLabel: string;
  dueDate?: string;
  payload: { assign_to_role: 'agent' } | { other_user_id: number; assign_to_role: 'other' };
};

export const useOneOffTaskInput = () => {
  const { assigneeBind, assigneeOptions, selectedOption, initializeAssignee } = useTaskAssigneeOptions();

  const isExpanded = ref(false);
  const showDatePicker = ref(false);
  const taskName = ref('');
  const dueDateString = ref('');
  const inputEl = ref<HTMLElement | null>(null);
  const isSubmitting = ref(false);

  const isEditing = computed(() => isExpanded.value && taskName.value.trim() !== '');

  const expand = async () => {
    isExpanded.value = true;
    await nextTick();
    inputEl.value?.focus();
  };

  const reset = () => {
    taskName.value = '';
    dueDateString.value = '';
    showDatePicker.value = false;
    initializeAssignee();
    isExpanded.value = false;
  };

  const validate = (): boolean => {
    if (!taskName.value.trim()) {
      toast.error('Task name is required');
      return false;
    }
    if (dueDateString.value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dueDate = new Date(dueDateString.value + 'T00:00:00');
      if (dueDate < tomorrow) {
        toast.error('Due date must be in the future');
        return false;
      }
    }
    return true;
  };

  const toPayload = (): OneOffTaskInputPayload => {
    const option = selectedOption();
    const label = option.name === 'Assign to me' ? 'Me' : option.name === 'Assign to agent' ? 'Agent' : option.name;

    return {
      name: taskName.value,
      assigneeLabel: label,
      dueDate: dueDateString.value,
      payload: option.payload,
    };
  };

  const submit = async (contactId: number): Promise<boolean> => {
    if (isSubmitting.value) return false;
    isSubmitting.value = true;
    try {
      const payload = toPayload();
      const response = await Api.OneOffTaskAssignments.createOneOfTaskAssignment(contactId, payload);
      if (response.status === 'success') {
        reset();
        toast.success('Task created');
        return true;
      }
      toast.error('Failed to create task', { description: response.messages[0] });
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    isExpanded,
    showDatePicker,
    taskName,
    dueDateString,
    assigneeBind,
    assigneeOptions,
    isEditing,
    isSubmitting,
    inputEl,
    expand,
    reset,
    validate,
    toPayload,
    submit,
  };
};

export type UseOneOffTaskInputReturn = ReturnType<typeof useOneOffTaskInput>;
